import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import moment from 'moment';
import React, { useMemo } from 'react';
import { ISPULine } from '../../../../@types/model/transformer/tariffs/tariffs';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../../@types/model/transformer/invoice/transformerInvoice';
import { DATE_INPUT_FORMAT_DEFAULT, DATE_REGEX } from '../../../../appConstants';
import DateTextInput from '../../../customComponents/input/DateTextInput';

interface ISPUCapturingFormTableRowProps {
    invoice : ITransformerInvoice<ISPULine>;
    index : number;
    lineIndex : number;
    line : ISPULine;

    isSubmitting ?: boolean;
    handleChange(e : React.ChangeEvent<unknown>) : void;
    handleBlur(e : React.FocusEvent<unknown>) : void;
    setFieldValue : (field : string, value : unknown, shouldValidate ?: boolean) => void;

    multi ?: boolean;
    selected ?: boolean;

    disabled ?: boolean;
}

const SPUCapturingFormTableRow = (props : ISPUCapturingFormTableRowProps) : React.ReactElement => {

    const calculateTotal = (invoice : ITransformerInvoice<ISPULine>) : void => {
        const value = TransformerInvoiceHelper.calculateSPUTotal(invoice);

        props.setFieldValue(`${props.index}`, value, true);
    };

    const onFieldChange = (event : React.ChangeEvent<HTMLInputElement>) : void => {
        if (!event.currentTarget.value) return;
        
        props.handleChange(event);

        const invoice : ITransformerInvoice<ISPULine> = { 
            ...props.invoice,
            lines: [...props.invoice.lines],
        };

        const field = event.currentTarget.name.split('.')[2];

        const line = invoice.lines[props.lineIndex];
        (line[field as keyof ISPULine] as number) = Number(event.currentTarget.value);

        calculateTotal(invoice as ITransformerInvoice<ISPULine>);
    };

    const onInvoiceFieldChange = (event : React.ChangeEvent<HTMLInputElement>) : void => {
        const name = event.currentTarget.name.split('.')[1];
        const value = Number(event.currentTarget.value);
        
        props.handleChange(event);
        
        if (!event.currentTarget.value) return;

        setTimeout(() => {
            const invoice : ITransformerInvoice<ISPULine> = { ...props.invoice, lines: [...props.invoice.lines] };
            (invoice[name as keyof ITransformerInvoice<ISPULine>] as number) = value;
    
            calculateTotal(invoice as ITransformerInvoice<ISPULine>);
        });
    };

    const getDueDateValid = useMemo<boolean>(() => {
        return DATE_REGEX.test(props.invoice.paymentDueDate ?? '') && moment.utc(props.invoice.paymentDueDate ?? '', DATE_INPUT_FORMAT_DEFAULT, true).isValid();
    }, [props.invoice.paymentDueDate]);

    const isValidDate = getDueDateValid;
    const disabled = props.disabled || props.isSubmitting;

    return (
        <TableRow selected={props.selected} className={`data-row ${props.multi ? 'multi' : ''}`}>
            <TableCell align='center' className='waypoint'>
                {props.line.transformer.EPNumber}
            </TableCell>
            <TableCell align='center' className='account'>
                {props.line.transformer.AccountNumber}
            </TableCell>
            <TableCell className='total-usage' align='center'>
                <input value={props.line.totalUsage}
                    disabled={disabled}
                    autoComplete='off'
                    name={`${props.index}.lines[${props.lineIndex}].totalUsage`}
                    onChange={onFieldChange}
                    onBlur={props.handleBlur}
                    type='number'
                    min='0'
                    step='any'
                    className='tac'
                />
            </TableCell>
            <TableCell className='' align='center'>
                <input value={props.line.days}
                    disabled={disabled}
                    autoComplete='off'
                    name={`${props.index}.lines[${props.lineIndex}].days`}
                    onChange={onFieldChange}
                    onBlur={props.handleBlur}
                    type='number'
                    min='0'
                    step='any'
                    className='tac'
                />
            </TableCell>
            {
                props.lineIndex === 0 &&
                <TableCell className='' align='center' rowSpan={2}>
                    <DateTextInput value={props.invoice.paymentDueDate ?? ''}
                        disabled={disabled}
                        autoComplete='off'
                        id={`${props.index}.paymentDueDate`}
                        name={`${props.index}.paymentDueDate`}
                        setFieldValue={props.setFieldValue}
                        onBlur={props.handleBlur}
                        className={`${isValidDate ? '' : 'cr'} tac`} />
                </TableCell>
            }
            {
                props.lineIndex === 0 &&
                <TableCell className='' align='center' rowSpan={2}>
                    <input value={props.invoice.credit}
                        disabled={disabled}
                        autoComplete='off'
                        name={`${props.index}.credit`}
                        onChange={onInvoiceFieldChange}
                        onBlur={props.handleBlur}
                        type='number'
                        step='any'
                        className='tac'
                    />
                </TableCell>
            }
            {
                props.lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    {props.invoice.totalCharge}
                </TableCell>
            }
            {
                props.lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    <input value={props.invoice.vatCharge}
                        disabled={props.isSubmitting || disabled}
                        autoComplete='off'
                        name={`${props.index}.vatCharge`}
                        onChange={props.handleChange}
                        type='number'
                        step='any'
                        className='tac'
                    />
                </TableCell>
            }
            {
                props.lineIndex === 0 &&
                <TableCell className={`invoice-total ${disabled ? 'disabled' : ''}`} rowSpan={2}>
                    {props.invoice.totalDue}
                </TableCell>
            }
            {
                props.lineIndex === 0 &&
                <TableCell className='invoice-total' rowSpan={2}>
                    <input value={props.invoice.totalBilled}
                        disabled={disabled}
                        autoComplete='off'
                        id={`${props.index}.totalBilled`}
                        name={`${props.index}.totalBilled`}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        type='number'
                        min='0'
                        step='0.01' />
                </TableCell>
            }
        </TableRow>
    );
};

export default SPUCapturingFormTableRow;
